/**
 * GroupByChip — a single GROUP BY chip, extracted from `GroupBySection`
 * (Plan 8 T4, §2.7 S12) so the drag-reorder wiring lives in its own module.
 * Mirrors `SelectColumnChip`'s `useSortable` idiom: the whole chip is the
 * drag handle (no separate grip), `sortable={false}` renders the same markup
 * without the drag affordance — a chip is not a disabled button, so it gets
 * no tooltip when non-draggable (matches the shipped SELECT-chip precedent).
 *
 * Preserves the out-of-scope marking, testids (`vqe-group-by-item-*`), and
 * `RemoveButton` wiring from the pre-extraction inline `<li>` verbatim.
 */
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { RemoveButton } from '../common/RemoveButton';
import { VqeTooltip } from '../common/VqeTooltip';

export interface GroupByChipProps {
  readonly id: string;
  readonly index: number;
  readonly text: string;
  readonly outOfScope: boolean;
  readonly sortable: boolean;
  readonly removable: boolean;
  readonly onRemove?: () => void;
}

export function GroupByChip({
  id,
  index,
  text,
  outOfScope,
  sortable,
  removable,
  onRemove,
}: GroupByChipProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: !sortable,
  });
  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  } as const;

  const tone = outOfScope ? 'border-red-500/60 bg-red-500/10' : 'border-border bg-muted/30';
  const grab = sortable ? 'cursor-grab active:cursor-grabbing touch-none ' : '';

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-1.5 rounded-md border px-2 py-1 ${grab}${tone}`}
      data-testid={`vqe-group-by-item-${index}`}
      data-out-of-scope={outOfScope ? 'true' : 'false'}
      {...(sortable ? attributes : {})}
      {...(sortable ? listeners : {})}
    >
      <span
        className={outOfScope ? 'font-mono text-xs text-red-600 dark:text-red-400' : 'font-mono text-xs'}
        data-testid={`vqe-group-by-item-text-${index}`}
      >
        {text}
      </span>
      {outOfScope ? (
        <VqeTooltip
          selfProvider
          content="This column references a table that is not in FROM/JOIN. Add the table or remove the entry."
        >
          <span
            className="shrink-0 cursor-help rounded bg-red-500/15 px-1 py-0 text-[9px] font-semibold uppercase tracking-wide text-red-600 dark:text-red-400"
            data-testid={`vqe-group-by-out-of-scope-${index}`}
          >
            unknown table
          </span>
        </VqeTooltip>
      ) : null}
      {/* stopPropagation keeps a click on Remove from also starting a drag —
          the listeners above are spread on the whole `<li>`. */}
      <span onPointerDown={(e) => e.stopPropagation()}>
        <RemoveButton
          testId={`vqe-group-by-remove-${index}`}
          ariaLabel="Remove group-by column"
          disabled={!removable}
          tooltip={removable ? 'Remove column from GROUP BY' : undefined}
          onClick={removable ? onRemove : undefined}
        />
      </span>
    </li>
  );
}
